import React, { useEffect, useState } from 'react';
import PaginatedTable from 'src/components/paginated-table/paginatedTable';
import {
  PaginatedTableColumn,
  PaginatedTableColumnType,
  ITableMenuAction,
  TableFilterType
} from 'src/components/paginated-table';
import { DataService } from '../../services/data-service';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import { Box } from '@mui/material';
import WarehouseDrawer from './warehouse-drawer';
import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import { Delete } from '@mui/icons-material';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { useNavigate } from 'react-router-dom';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  virtual: boolean;
  location: string;
  dimensions: string;
  isDefault: boolean;
}

const WarehouseListingSimple: React.FC = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateData, setUpdateData] = useState<Warehouse | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Export handler
  const handleExport = async () => {
    try {
      const response = await DataService.get('api/Warehouse/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'warehouses_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        dispatch(showAlert({ message: 'Export successful', type: 'success' }));
      } else {
        dispatch(showAlert({ message: 'Export failed', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Export failed', type: 'error' }));
    }
  };

  const columns: PaginatedTableColumn[] = [
    {
      id: 'code',
      label: 'Code',
      type: PaginatedTableColumnType.CLICKABLE,
      onClick: (id: string, row: any) => navigate(`/warehouse/${row.id}`),
      searchable: true,
    },
    {
      id: 'name',
      label: 'Name',
      type: PaginatedTableColumnType.TEXT,
      searchable: true,
    },
    {
      id: 'virtual',
      label: 'Virtual',
      type: PaginatedTableColumnType.CUSTOM,
      render: (value: boolean) => (
        <Box
          sx={{
            display: 'inline-block',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: value ? 'success.light' : 'error.light',
            color: value ? 'success.dark' : 'error.dark',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {value ? 'Yes' : 'No'}
        </Box>
      ),
    },
       {
      id: 'isDefault',
      label: 'Default',
      type: PaginatedTableColumnType.CUSTOM,
      render: (value: boolean) => (
        <Box
          sx={{
            display: 'inline-block',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: value ? 'success.light' : 'error.light',
            color: value ? 'success.dark' : 'error.dark',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {value ? 'Yes' : 'No'}
        </Box>
      ),
    },
    {
      id: 'location',
      label: 'Location',
      type: PaginatedTableColumnType.TEXT,
      searchable: true,
    },
        {
      id: 'dimensions',
      label: 'Dimensions',
      type: PaginatedTableColumnType.TEXT,
      searchable: true,
    },
    {
      id: 'storageUnitsCount',
      label: 'Storage Units',
      type: PaginatedTableColumnType.TEXT,
      searchable: true,
    },
        {
      id: 'inventoryItemsCount',
      label: 'Inventory Items',
      type: PaginatedTableColumnType.TEXT,
      searchable: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: PaginatedTableColumnType.ACTION,
      actions: [
        {
          key: 'edit',
          onClick: (id: string, row: Warehouse) => {
            setUpdateData(row);
            setOpenEdit(true);
          },
          icon: <EditIcon fontSize="small" />,
          label: 'Edit',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.EDIT_WAREHOUSE, userRole),
        },
        {
          key: 'setDefault',
          onClick: (id: string, row: any) => handleSetDefault(row.id),
          icon: <StarIcon fontSize="small" />,
          label: 'Set Default',
          color: 'primary',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.EDIT_WAREHOUSE, userRole),
        },
        {
          key: 'delete',
          onClick: (id: string) => setDeleteId(id),
          icon: <Delete fontSize='small' />,
          label: 'Delete',
          color: 'error',
          visible: () => PermissionService.getInstance().isAllowed(ACTION.DELETE_WAREHOUSE, userRole),
        }
      ]
    }
  ];

  // Table actions
  const actions: ITableMenuAction[] = [
    {
      key: 'addWarehouse',
      title: 'Add Warehouse',
      icon: <AddIcon />,
      disabled: () => false,
      onClick: () => setOpenAdd(true),
      visible: () => PermissionService.getInstance().isAllowed(ACTION.CREATE_WAREHOUSE, userRole),
    },
    {
      key: 'export',
      title: 'Export',
      icon: <DownloadIcon />,
      disabled: () => false,
      onClick: handleExport,
    }
  ];

  // Table filters
  const filters = [
    {
      id: 'virtual',
      label: 'Virtual',
      column: 'virtual',
      type: TableFilterType.SELECT,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
    }
  ];

  // Drawer handlers
  const handleDrawerClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setUpdateData(undefined);
  };

  const handleDrawerSuccess = () => {
    handleDrawerClose();
    setRefreshKey(prev => prev + 1);
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      const res = await DataService.delete(`api/warehouse/delete/${deleteId}`, {});

      if (res.ok) {
        dispatch(showAlert({ message: 'Warehouse deleted successfully!', type: 'success' }));
        setRefreshKey(prev => prev + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to delete warehouse!', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Failed to delete warehouse!', type: 'error' }));
    }
    setDeleteLoading(false);
    setDeleteId(null);
  };

  // Set Default handler
  const handleSetDefault = async (id: string) => {
    try {
      const res = await DataService.post(`api/Warehouse/${id}/set-default`, {});
      const body = await res.json();

      if (body.success === false) {
        dispatch(showAlert({ message: body.message || 'Failed to set default warehouse', type: 'error' }));
      } else {
        dispatch(showAlert({ message: 'Default warehouse set successfully!', type: 'success' }));
        setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Failed to set default warehouse', type: 'error' }));
    }
  };

  return (
    <Box>
      <PaginatedTable
        key={refreshKey}
        title="Warehouses"
        // subtitle="Manage your warehouse locations"
        columns={columns}
        data={{}}
        actions={actions}
        filters={filters as any}
        useBackendPagination={true}
        apiEndpoint="api/Warehouse"
        cacheTimeout={300000}
        defaultState={{
          rowsPerPage: 10,
        }}
        selection={{
          showCheckbox: true,
          selectionMode: 'multiple',
        }}
        stickyHeader={true}
        striped={true}
        hoverable={true}
        columnChooser={true}
        collapsible={true}
        wmsMode={true}
      />
      
      {(openAdd || openEdit) && (
        <WarehouseDrawer
          open={openAdd || openEdit}
          onClose={handleDrawerClose}
          updateData={openEdit && updateData ? { ...updateData, Code: updateData.code ?? '', Name: updateData.name ?? '', Virtual: updateData.virtual ?? false, IsDefault: updateData.isDefault ?? false,Location: updateData.location ?? '', Dimensions: updateData.dimensions ?? '' } : undefined}
          onSuccess={handleDrawerSuccess}
        />
      )}
      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Warehouse"
        message="Are you sure you want to delete this warehouse? This action cannot be undone."
        // loading={deleteLoading}
      />
    </Box>
  );
};

export default WarehouseListingSimple;